import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { AdminSidebar } from '../components/AdminSidebar';
import { useNavigate } from 'react-router-dom';

export default function AdminPanel() {
    const { api, logout } = useAuth();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modal states
    const [showAddClient, setShowAddClient] = useState(false);
    const [newClient, setNewClient] = useState({
        name: '', email: '', password: '', client_slug: '', business_name: '', gstin: '', default_fy: 'FY 2024-2025'
    });

    const fetchClients = async () => {
        try {
            const res = await api.get('/api/admin/clients');
            setClients(res.data);
        } catch (err) {
            toast.error("Failed to load clients");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const handleAddClient = async (e) => {
        e.preventDefault();
        try {
            await api.post('/api/admin/clients', newClient);
            toast.success("Client created successfully");
            setShowAddClient(false);
            setNewClient({ name: '', email: '', password: '', client_slug: '', business_name: '', gstin: '', default_fy: 'FY 2024-2025' });
            fetchClients();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create client");
        }
    };

    const [uploadingLogo, setUploadingLogo] = useState({});

    const handleLogoUpload = async (clientId, e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setUploadingLogo({...uploadingLogo, [clientId]: true});
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            await api.post(`/api/admin/clients/${clientId}/logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("Logo uploaded successfully");
            fetchClients();
        } catch (err) {
            toast.error("Failed to upload logo");
        } finally {
            setUploadingLogo({...uploadingLogo, [clientId]: false});
        }
    };

    const handleResetPassword = async (clientId) => {
        const newPwd = prompt("Enter new password for this client:");
        if (!newPwd) return;
        try {
            await api.put(`/api/admin/clients/${clientId}`, { password: newPwd });
            toast.success("Password reset successfully");
        } catch (err) {
            toast.error("Failed to reset password");
        }
    };
    
    const handleDeleteClient = async (clientId) => {
        if (!window.confirm("Are you sure you want to delete this client? This cannot be undone.")) return;
        try {
            await api.delete(`/api/admin/clients/${clientId}`);
            toast.success("Client deleted");
            fetchClients();
        } catch (err) {
            toast.error("Failed to delete client");
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="h-16 bg-[#0A192F] text-white flex items-center justify-between px-6 border-b border-[#EAB308]">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#EAB308] rounded text-[#0A192F] font-bold flex items-center justify-center">RC</div>
                    <span className="font-['Cabinet_Grotesk'] font-bold tracking-wide">Super Admin Panel</span>
                </div>
                <button onClick={async () => { await logout(); navigate('/'); }} className="text-sm text-slate-300 hover:text-white transition-colors">Sign Out</button>
            </header>

            <div className="flex">
                {/* Admin Sidebar */}
                <AdminSidebar />

                <main className="flex-1 p-6 max-w-7xl space-y-8">
                <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <div>
                        <h1 className="text-2xl font-bold text-[#0F284E]">Client Management</h1>
                        <p className="text-slate-500 text-sm mt-1">Manage tenants, reset credentials, and monitor platform.</p>
                    </div>
                    <button 
                        onClick={() => setShowAddClient(true)}
                        data-testid="admin-add-client-button"
                        className="bg-[#0A192F] hover:bg-[#0F284E] text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-colors"
                    >
                        + Add New Client Tenant
                    </button>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <table className="w-full text-sm text-left" data-testid="admin-client-list-table">
                        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-mono tracking-wider text-slate-600">
                            <tr>
                                <th className="px-6 py-4">Business Name & Slug</th>
                                <th className="px-6 py-4">Contact Info</th>
                                <th className="px-6 py-4">Bank Details</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-500">Loading clients...</td></tr>
                            ) : clients.map(client => (
                                <tr key={client._id} className="hover:bg-slate-50/50">
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-[#0F284E]">{client.business_name}</div>
                                        <div className="text-xs font-mono text-slate-500">/{client.client_slug}</div>
                                        {client.gstin && <div className="text-xs text-emerald-600 mt-1 font-medium bg-emerald-50 inline-block px-1.5 py-0.5 rounded">GST: {client.gstin}</div>}
                                        {client.pan_number && <div className="text-xs text-blue-600 mt-1 font-medium bg-blue-50 inline-block px-1.5 py-0.5 rounded ml-1">PAN: {client.pan_number}</div>}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-slate-700">{client.name}</div>
                                        <div className="text-xs text-slate-500">{client.email}</div>
                                        {client.address && <div className="text-xs text-slate-400 mt-1 truncate max-w-[150px]" title={client.address}>{client.address}</div>}
                                    </td>
                                    <td className="px-6 py-4">
                                        {client.bank_name ? (
                                            <div className="text-xs">
                                                <div className="font-medium text-slate-700">{client.bank_name}</div>
                                                <div className="text-slate-500">A/C: {client.account_number}</div>
                                                <div className="text-slate-500">IFSC: {client.ifsc_code}</div>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-400 italic">Not provided</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-right space-x-2">
                                        <div className="flex items-center gap-2 justify-end mb-2">
                                            {client.logo_url && <img src={client.logo_url.startsWith('data:') ? client.logo_url : (process.env.REACT_APP_BACKEND_URL + client.logo_url)} alt="Logo" data-testid="client-logo-preview" className="w-8 h-8 rounded border border-slate-200 object-cover" />}
                                            <div className="relative">
                                                <input 
                                                    type="file" 
                                                    accept="image/*" 
                                                    data-testid="admin-upload-logo-input"
                                                    onChange={(e) => handleLogoUpload(client._id, e)}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                />
                                                <button 
                                                    disabled={uploadingLogo[client._id]}
                                                    data-testid="admin-upload-logo-button"
                                                    className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded transition-colors font-medium border border-emerald-200 whitespace-nowrap"
                                                >
                                                    {uploadingLogo[client._id] ? 'Uploading...' : (client.logo_url ? 'Change Logo' : 'Upload Logo')}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => handleResetPassword(client._id)}
                                                data-testid="admin-reset-pwd-button"
                                                className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded transition-colors font-medium border border-slate-200"
                                            >
                                                Reset Pwd
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteClient(client._id)}
                                                data-testid="admin-delete-client-button"
                                                className="text-xs bg-rose-50 hover:bg-rose-100 text-rose-700 px-3 py-1.5 rounded transition-colors font-medium border border-rose-200"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {showAddClient && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border border-slate-200">
                            <h3 className="text-xl font-bold text-[#0F284E] mb-4 border-b border-slate-100 pb-4">Create New Tenant</h3>
                            <form onSubmit={handleAddClient} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Business Name</label>
                                        <input type="text" required value={newClient.business_name} onChange={e => setNewClient({...newClient, business_name: e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Client Slug (URL)</label>
                                        <input type="text" required value={newClient.client_slug} onChange={e => setNewClient({...newClient, client_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F] font-mono" placeholder="company-name" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Contact Name</label>
                                        <input type="text" required value={newClient.name} onChange={e => setNewClient({...newClient, name: e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">GSTIN (Optional)</label>
                                        <input type="text" value={newClient.gstin} onChange={e => setNewClient({...newClient, gstin: e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Login Email</label>
                                    <input type="email" required value={newClient.email} onChange={e => setNewClient({...newClient, email: e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Initial Password</label>
                                    <input type="password" required value={newClient.password} onChange={e => setNewClient({...newClient, password: e.target.value})} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                </div>
                                <div className="pt-4 flex justify-end gap-2 border-t border-slate-100">
                                    <button type="button" onClick={() => setShowAddClient(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors">Cancel</button>
                                    <button type="submit" className="px-4 py-2 rounded-lg bg-[#EAB308] hover:bg-[#CA8A04] text-[#0A192F] font-bold shadow-sm transition-colors">Create Tenant</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </main>
            </div>
        </div>
    );
}
